import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/api';
import { ProcurementFinancialDashboard, SupplierStatement, SupplierStatementOption } from '../../types';

const money = (value: number) => `${value.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} грн`;
const localDate = (date: Date) => {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};
const th: React.CSSProperties = { padding: 9, textAlign: 'left', background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-border)', whiteSpace: 'nowrap' };
const td: React.CSSProperties = { padding: 8, borderBottom: '1px solid var(--crm-table-row-border)' };

const ProcurementFinancialDashboardPage: React.FC = () => {
  const [data, setData] = useState<ProcurementFinancialDashboard | null>(null);
  const [periodDays, setPeriodDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statementOptions, setStatementOptions] = useState<SupplierStatementOption[]>([]);
  const [statementSupplierId, setStatementSupplierId] = useState('');
  const [statementFrom, setStatementFrom] = useState(localDate(new Date(new Date().setDate(new Date().getDate() - 90))));
  const [statementTo, setStatementTo] = useState(localDate(new Date()));
  const [statement, setStatement] = useState<SupplierStatement | null>(null);
  const [statementLoading, setStatementLoading] = useState(false);
  const [paymentDetailsFilter, setPaymentDetailsFilter] = useState<'all' | 'ready' | 'missing'>('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [dashboardResponse, suppliersResponse] = await Promise.all([
        api.get<ProcurementFinancialDashboard>('/procurementfinancial', {
          params: { paidPeriodDays: periodDays },
        }),
        api.get<SupplierStatementOption[]>('/procurementfinancial/statement-suppliers'),
      ]);
      setData(dashboardResponse.data);
      setStatementOptions(suppliersResponse.data);
      setStatementSupplierId(current => current || (suppliersResponse.data[0]?.supplierId.toString() ?? ''));
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Не вдалося завантажити фінанси закупівель.');
    } finally {
      setLoading(false);
    }
  }, [periodDays]);

  useEffect(() => { load(); }, [load]);

  const loadStatement = async () => {
    if (!statementSupplierId) {
      setError('Оберіть постачальника для звірки.');
      return;
    }
    setStatementLoading(true);
    setError('');
    try {
      const response = await api.get<SupplierStatement>(
        `/procurementfinancial/supplier-statement/${statementSupplierId}`,
        { params: { from: statementFrom, to: statementTo } },
      );
      setStatement(response.data);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Не вдалося сформувати звірку.');
    } finally {
      setStatementLoading(false);
    }
  };

  const exportStatement = async () => {
    if (!statementSupplierId) return;
    let url: string | null = null;
    try {
      const response = await api.get(
        `/procurementfinancial/supplier-statement/${statementSupplierId}/export-excel`,
        { params: { from: statementFrom, to: statementTo }, responseType: 'blob' },
      );
      url = window.URL.createObjectURL(new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Звірка_${statementSupplierId}_${statementFrom}_${statementTo}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Не вдалося експортувати звірку.');
    } finally {
      if (url) window.URL.revokeObjectURL(url);
    }
  };

  const timeline = useMemo(() => data?.timeline.slice(0, 50) ?? [], [data]);
  const supplierRows = useMemo(() => data?.suppliers.filter(row =>
    paymentDetailsFilter === 'all' ||
    (paymentDetailsFilter === 'ready' ? row.hasPaymentDetails : !row.hasPaymentDetails)) ?? [],
  [data, paymentDetailsFilter]);
  const card = (title: string, value: number, color: string, subtitle?: string) => (
    <div style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: 14, borderLeft: `4px solid ${color}`, boxShadow: 'var(--crm-shadow-sm)' }}>
      <div style={{ color: 'var(--crm-text-muted)', fontSize: 11 }}>{title}</div>
      <div style={{ color, fontSize: 23, fontWeight: 700, marginTop: 3 }}>{money(value)}</div>
      {subtitle && <div style={{ color: 'var(--crm-text-muted)', fontSize: 11, marginTop: 3 }}>{subtitle}</div>}
    </div>
  );

  return (
    <div className="page-shell page-shell--wide" style={{ maxWidth: 1450, margin: '0 auto' }}>
      <h2 style={{ color: 'var(--crm-text)', marginTop: 0 }}>Фінанси закупівель</h2>
      <p style={{ color: 'var(--crm-text-muted)' }}>
        Відкриті зобов'язання — ще не отримані товари; кредиторська заборгованість — отримані, але неоплачені накладні.
      </p>
      <div className="procurement-finance-period-controls" style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 16 }}>
        <label>Період оплачених накладних:
          <select value={periodDays} onChange={event => setPeriodDays(Number(event.target.value))} style={{ marginLeft: 8, padding: 6 }}>
            <option value={7}>7 днів</option>
            <option value={30}>30 днів</option>
            <option value={90}>90 днів</option>
            <option value={365}>365 днів</option>
          </select>
        </label>
        <button onClick={load} disabled={loading} style={{ border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-sm)', background: 'var(--crm-info-bg)', color: 'var(--crm-info)', padding: '7px 14px', cursor: 'pointer' }}>
          {loading ? 'Оновлення...' : 'Оновити'}
        </button>
      </div>
      {error && <div role="alert" style={{ background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', padding: 10, borderRadius: 'var(--crm-radius-sm)', marginBottom: 12 }}>{error}</div>}
      {data && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12, marginBottom: 18 }}>
            {card('Загальний exposure', data.totalExposure, 'var(--crm-text)', `${data.openOrderCount} відкритих ордерів · ${data.unpaidReceiptCount} неоплачених накладних`)}
            {card('Відкриті зобов’язання', data.openCommitment, 'var(--crm-info)')}
            {card('Кредиторська заборгованість', data.accountsPayable, 'var(--crm-blocked)')}
            {card('Прострочена кредиторка', data.overdueAccountsPayable, 'var(--crm-danger)')}
            {card('Погоджено до оплати', data.approvedPaymentAmount, 'var(--crm-info)', `${data.approvedPaymentBatchCount} реєстрів`)}
            {card('Ще не заплановано', data.unplannedAccountsPayable, 'var(--crm-warning)')}
            {card('Заблоковано без реквізитів', data.paymentBlockedAccountsPayable, 'var(--crm-danger)', `${data.paymentBlockedSupplierCount} постачальників`)}
            {card('Прострочено без плану', data.overdueUnplannedAccountsPayable, 'var(--crm-danger)')}
            {card('Заплановано до 7 днів', data.plannedNext7Days, 'var(--crm-success)')}
            {card('Заплановано до 30 днів', data.plannedNext30Days, 'var(--crm-info)')}
            {card('Чернетки оплат', data.draftPaymentAmount, 'var(--crm-blocked)')}
            {card('Прострочені поставки', data.overdueCommitment, 'var(--crm-danger)', `${data.overdueOrderCount} ордерів`)}
            {card('Очікується до 7 днів', data.expectedNext7Days, 'var(--crm-warning)')}
            {card('Очікується до 30 днів', data.expectedNext30Days, 'var(--crm-success)')}
            {card(`Оплачено за ${periodDays} днів`, data.paidInPeriod, 'var(--crm-success)')}
            {card('Чернетки зобов’язань', data.draftCommitment, 'var(--crm-text-muted)', 'Ще не відправлені постачальникам')}
          </div>

          {(data.bankingReviewQueue.pendingCount > 0 || data.bankingReviewQueue.rejectedCount > 0) && (
            <section style={{
              background: data.bankingReviewQueue.overdueCount > 0 ? 'var(--crm-danger-bg)' : 'var(--crm-warning-bg)',
              border: `1px solid ${data.bankingReviewQueue.overdueCount > 0 ? 'var(--crm-danger)' : 'var(--crm-warning)'}`,
              borderRadius: 'var(--crm-radius-md)',
              padding: 14,
              marginBottom: 20,
              color: 'var(--crm-text)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 14, flexWrap: 'wrap' }}>
                <div>
                  <h3 style={{ color: data.bankingReviewQueue.overdueCount > 0 ? 'var(--crm-danger)' : 'var(--crm-warning)', margin: '0 0 6px' }}>Потрібна перевірка банківських реквізитів</h3>
                  <div style={{ color: 'var(--crm-text)' }}>
                    Очікують: <strong>{data.bankingReviewQueue.pendingCount}</strong>
                    {' · '}прострочено понад {data.bankingReviewQueue.overdueAfterHours} год:
                    {' '}<strong>{data.bankingReviewQueue.overdueCount}</strong>
                    {' · '}відхилено: <strong>{data.bankingReviewQueue.rejectedCount}</strong>
                  </div>
                  <div style={{ color: 'var(--crm-danger)', marginTop: 5 }}>
                    Заблоковано без плану: <strong>{money(data.bankingReviewQueue.pendingUnplannedAccountsPayable)}</strong>
                    {' · '}прострочено: <strong>{money(data.bankingReviewQueue.overdueUnplannedAccountsPayable)}</strong>
                  </div>
                </div>
                <Link
                  to="/management/suppliers"
                  style={{ background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', borderRadius: 'var(--crm-radius-sm)', padding: '8px 14px', textDecoration: 'none', fontWeight: 600 }}
                >
                  Відкрити чергу
                </Link>
              </div>
              {data.bankingReviewQueue.items.length > 0 && (
                <div style={{ overflowX: 'auto', marginTop: 12 }}>
                  <table className="crm-table" style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--crm-surface)' }}>
                    <thead><tr>
                      <th style={th}>Пріоритет</th>
                      <th style={th}>Постачальник</th>
                      <th style={th}>Змінено</th>
                      <th style={{ ...th, textAlign: 'right' }}>Без плану</th>
                      <th style={{ ...th, textAlign: 'right' }}>Прострочено</th>
                      <th style={th}>Найближчий строк</th>
                    </tr></thead>
                    <tbody>
                      {data.bankingReviewQueue.items.slice(0, 5).map(item => (
                        <tr key={item.supplierId}>
                          <td style={{ ...td, color: item.isOverdue ? 'var(--crm-danger)' : 'var(--crm-warning)', fontWeight: 700 }}>
                            {item.isOverdue ? `${item.ageHours} год · прострочено` : item.status === 'Rejected' ? 'Відхилено' : `${item.ageHours} год`}
                            {item.riskLevel !== 'Standard' && (
                              <div style={{ fontSize: 11, color: item.riskLevel === 'Critical' ? 'var(--crm-danger)' : 'var(--crm-warning)' }}>
                                {item.riskLevel === 'Critical' ? 'Критичний ризик' : 'Високий ризик'}
                              </div>
                            )}
                          </td>
                          <td style={td}><strong>{item.supplierName}</strong></td>
                          <td style={{ ...td, color: 'var(--crm-text-muted)' }}>
                            {item.changes.length > 0 ? item.changes.map(change => change.label).join(', ') : '—'}
                          </td>
                          <td style={{ ...td, textAlign: 'right' }}>{money(item.unplannedAccountsPayable)}</td>
                          <td style={{ ...td, textAlign: 'right', color: item.overdueUnplannedAccountsPayable > 0 ? 'var(--crm-danger)' : 'var(--crm-text-muted)' }}>
                            {money(item.overdueUnplannedAccountsPayable)}
                          </td>
                          <td style={td}>
                            {item.nearestPaymentDueDate ? new Date(item.nearestPaymentDueDate).toLocaleDateString('uk-UA') : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          <h3 style={{ color: 'var(--crm-text)' }}>Aging кредиторської заборгованості</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 20 }}>
            {card('Не прострочено', data.agingCurrent, 'var(--crm-success)')}
            {card('1–30 днів', data.aging1To30Days, 'var(--crm-warning)')}
            {card('31–60 днів', data.aging31To60Days, 'var(--crm-warning)')}
            {card('61–90 днів', data.aging61To90Days, 'var(--crm-danger)')}
            {card('Понад 90 днів', data.agingOver90Days, 'var(--crm-danger)')}
          </div>

          <div className="procurement-finance-exposure-controls" style={{ display: 'flex', alignItems: 'end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <h3 style={{ color: 'var(--crm-text)' }}>Exposure за постачальниками</h3>
            <label style={{ marginBottom: 10 }}>Платіжні реквізити
              <select value={paymentDetailsFilter} onChange={event => setPaymentDetailsFilter(event.target.value as 'all' | 'ready' | 'missing')} style={{ display: 'block', padding: 6, minWidth: 220 }}>
                <option value="all">Усі постачальники</option>
                <option value="missing">Без реквізитів</option>
                <option value="ready">Готові до оплати</option>
              </select>
            </label>
          </div>
          <div style={{ overflowX: 'auto', marginBottom: 22 }}>
            <table className="crm-table" style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--crm-surface)' }}>
              <thead><tr>
                <th style={th}>Постачальник</th>
                <th style={{ ...th, textAlign: 'right' }}>Відкриті ордери</th>
                <th style={{ ...th, textAlign: 'right' }}>Зобов’язання</th>
                <th style={{ ...th, textAlign: 'right' }}>Прострочено</th>
                <th style={{ ...th, textAlign: 'right' }}>Неоплачені накладні</th>
                <th style={{ ...th, textAlign: 'right' }}>Кредиторка</th>
                <th style={{ ...th, textAlign: 'right' }}>Прострочена кредиторка</th>
                <th style={{ ...th, textAlign: 'right' }}>Погоджено</th>
                <th style={{ ...th, textAlign: 'right' }}>Без плану</th>
                <th style={{ ...th, textAlign: 'right' }}>Заблоковано</th>
                <th style={th}>Реквізити</th>
                <th style={{ ...th, textAlign: 'right' }}>Exposure</th>
              </tr></thead>
              <tbody>
                {supplierRows.map(row => (
                  <tr key={`${row.supplierId}-${row.supplierName}`} style={{ background: row.overdueOrderCount > 0 ? 'var(--crm-danger-bg)' : undefined }}>
                    <td style={td}><strong>{row.supplierName}</strong></td>
                    <td style={{ ...td, textAlign: 'right' }}>{row.openOrderCount}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{money(row.openCommitment)}</td>
                    <td style={{ ...td, textAlign: 'right', color: row.overdueCommitment > 0 ? 'var(--crm-danger)' : 'var(--crm-text-muted)' }}>{money(row.overdueCommitment)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{row.unpaidReceiptCount}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{money(row.accountsPayable)}</td>
                    <td style={{ ...td, textAlign: 'right', color: row.overdueAccountsPayable > 0 ? 'var(--crm-danger)' : 'var(--crm-text-muted)' }}>{money(row.overdueAccountsPayable)}</td>
                    <td style={{ ...td, textAlign: 'right', color: 'var(--crm-info)' }}>{money(row.approvedPaymentAmount)}</td>
                    <td style={{ ...td, textAlign: 'right', color: row.unplannedAccountsPayable > 0 ? 'var(--crm-warning)' : 'var(--crm-text-muted)' }}>{money(row.unplannedAccountsPayable)}</td>
                    <td style={{ ...td, textAlign: 'right', color: row.paymentBlockedAccountsPayable > 0 ? 'var(--crm-danger)' : 'var(--crm-text-muted)' }}>{money(row.paymentBlockedAccountsPayable)}</td>
                    <td style={{ ...td, color: row.hasPaymentDetails ? 'var(--crm-success)' : 'var(--crm-danger)', minWidth: 210 }}>
                      {row.hasPaymentDetails ? 'Готові' : row.paymentDetailsIssue}
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{money(row.totalExposure)}</td>
                  </tr>
                ))}
                {supplierRows.length === 0 && <tr><td colSpan={12} style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>Відкритих зобов'язань за фільтром немає</td></tr>}
              </tbody>
            </table>
          </div>

          <h3 style={{ color: 'var(--crm-text)' }}>Звірка з постачальником</h3>
          <section style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: 14, marginBottom: 22, boxShadow: 'var(--crm-shadow-sm)' }}>
            <div className="procurement-finance-statement-controls" style={{ display: 'flex', gap: 10, alignItems: 'end', flexWrap: 'wrap', marginBottom: statement ? 14 : 0 }}>
              <label>Постачальник
                <select value={statementSupplierId} onChange={event => { setStatementSupplierId(event.target.value); setStatement(null); }} style={{ display: 'block', padding: 6, minWidth: 260 }}>
                  <option value="">Оберіть постачальника</option>
                  {statementOptions.map(option => <option key={option.supplierId} value={option.supplierId}>{option.supplierName}</option>)}
                </select>
              </label>
              <label>Від<input type="date" value={statementFrom} onChange={event => setStatementFrom(event.target.value)} style={{ display: 'block', padding: 6 }} /></label>
              <label>До<input type="date" value={statementTo} onChange={event => setStatementTo(event.target.value)} style={{ display: 'block', padding: 6 }} /></label>
              <button onClick={loadStatement} disabled={statementLoading || !statementSupplierId} style={{ border: '1px solid var(--crm-blocked)', borderRadius: 'var(--crm-radius-sm)', background: 'var(--crm-blocked-bg)', color: 'var(--crm-blocked)', padding: '7px 14px', cursor: 'pointer' }}>
                {statementLoading ? 'Формування...' : 'Сформувати звірку'}
              </button>
              <button onClick={exportStatement} disabled={!statementSupplierId} style={{ border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-sm)', background: 'var(--crm-info-bg)', color: 'var(--crm-info)', padding: '7px 14px', cursor: 'pointer' }}>
                Excel
              </button>
            </div>
            {statement && <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10, marginBottom: 14 }}>
                {card('Початкове сальдо', statement.openingBalance, 'var(--crm-text-muted)')}
                {card('Накладні за період', statement.invoicedAmount, 'var(--crm-blocked)')}
                {card('Оплачено за період', statement.paidAmount, 'var(--crm-success)')}
                {card('Кінцеве сальдо', statement.closingBalance, statement.closingBalance > 0 ? 'var(--crm-danger)' : 'var(--crm-success)')}
              </div>
              <div style={{ overflowX: 'auto', maxHeight: 430 }}>
                <table className="crm-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={th}>Дата</th><th style={th}>Тип</th><th style={th}>Документ</th><th style={th}>Опис</th>
                    <th style={{ ...th, textAlign: 'right' }}>Нараховано</th><th style={{ ...th, textAlign: 'right' }}>Оплачено</th><th style={{ ...th, textAlign: 'right' }}>Сальдо</th>
                  </tr></thead>
                  <tbody>
                    {statement.entries.map(entry => <tr key={`${entry.type}-${entry.transactionId}`}>
                      <td style={td}>{new Date(entry.date).toLocaleDateString('uk-UA')}</td>
                      <td style={td}>{entry.type === 'Invoice' ? 'Накладна' : 'Оплата'}</td>
                      <td style={td}>{entry.documentNumber}</td>
                      <td style={td}>{entry.description}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{entry.debit ? money(entry.debit) : '—'}</td>
                      <td style={{ ...td, textAlign: 'right', color: entry.credit ? 'var(--crm-success)' : undefined }}>{entry.credit ? money(entry.credit) : '—'}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{money(entry.runningBalance)}</td>
                    </tr>)}
                    {statement.entries.length === 0 && <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>За вибраний період рухів немає</td></tr>}
                  </tbody>
                </table>
              </div>
            </>}
          </section>

          <h3 style={{ color: 'var(--crm-text)' }}>Календар зобов’язань</h3>
          <div style={{ overflowX: 'auto' }}>
            <table className="crm-table" style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--crm-surface)' }}>
              <thead><tr>
                <th style={th}>Дата</th><th style={th}>Тип</th><th style={th}>Документ</th>
                <th style={th}>Постачальник</th><th style={{ ...th, textAlign: 'right' }}>Сума</th>
              </tr></thead>
              <tbody>
                {timeline.map(item => (
                  <tr key={`${item.type}-${item.documentId}-${item.supplierId ?? 'none'}`} style={{ background: item.isOverdue ? 'var(--crm-danger-bg)' : undefined }}>
                    <td style={{ ...td, color: item.isOverdue ? 'var(--crm-danger)' : undefined }}>{new Date(item.date).toLocaleDateString('uk-UA')}{item.isOverdue ? ' · прострочено' : ''}</td>
                    <td style={td}>{item.type === 'Order' ? 'Очікувана поставка' : item.type === 'PaymentBatch' ? 'Погоджений платіж' : 'Незапланована накладна'}</td>
                    <td style={td}>{item.documentNumber}</td>
                    <td style={td}>{item.supplierName}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{money(item.amount)}</td>
                  </tr>
                ))}
                {timeline.length === 0 && <tr><td colSpan={5} style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>Календар порожній</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default ProcurementFinancialDashboardPage;
